import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Eye, EyeSlash, ClipboardText } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ModelosPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/plan-templates`); setTemplates(data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toggleActive = async (t) => {
    await axios.put(`${API}/api/plan-templates/${t.id}`, { active: !t.active });
    fetch();
  };
  const saveName = async () => {
    if (!editId) return;
    await axios.put(`${API}/api/plan-templates/${editId}`, { name: editName });
    toast.success('Nome atualizado');
    setEditId(null); fetch();
  };
  const del = async (id) => {
    const t = templates.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o modelo de plano "${t?.name || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/plan-templates/${id}`);
    toast.success('Modelo eliminado'); fetch();
  };

  const countMeals = (t) => {
    let c = 0;
    Object.values(t.meals || {}).forEach(v => { if (Array.isArray(v)) c += v.length; });
    return c;
  };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="modelos-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Modelos de Planos</h2>
          <p className="text-xs text-brand-muted mt-0.5">Planos guardados como modelo para reutilização</p>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <ClipboardText size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem modelos guardados.</p>
          <p className="text-brand-placeholder text-xs mt-1">Guarda um plano existente como modelo na página de planos.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map(t => (
            <div key={t.id} className={`bg-white border border-brand-border rounded-2xl p-4 ${!t.active ? 'opacity-50' : ''}`} data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {editId === t.id ? (
                    <div className="flex gap-2 items-center">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} className="rounded-lg border-brand-border text-sm h-8 flex-1" autoFocus />
                      <button onClick={saveName} className="text-brand-green text-xs font-medium">Guardar</button>
                      <button onClick={() => setEditId(null)} className="text-brand-muted text-xs">Cancelar</button>
                    </div>
                  ) : (
                    <h3 className="text-sm font-medium text-brand-text">{t.name}</h3>
                  )}
                  <div className="flex gap-3 mt-1 text-[10px] text-brand-muted">
                    <span>{countMeals(t)} alimentos</span>
                    <span>{Object.keys(t.meal_config || {}).length} refeições</span>
                    {t.water_goal && <span>{t.water_goal}L água</span>}
                    {!t.active && <span className="text-red-400">Inativo</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => toggleActive(t)} className="p-1 text-brand-placeholder hover:text-brand-green" title={t.active ? 'Desativar' : 'Ativar'}>
                    {t.active ? <Eye size={14} /> : <EyeSlash size={14} />}
                  </button>
                  <button onClick={() => { setEditId(t.id); setEditName(t.name); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                  <button onClick={() => del(t.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
